import { Box, IconButton, Tooltip } from '@mui/material';
import ClipboardDocumentIcon from '@heroicons/react/24/solid/ClipboardDocumentIcon';
import { FunctionComponent } from 'react';
import { useCopyToClipboard } from '@/pages/widgetEditor/hooks/useCopyToClipboard';

interface DocCodeProps {
    children: string;
}

/** Shared radius of every bordered block in the documentation. */
export const DOC_RADIUS = '12px';

/**
 * Monospace block for a snippet, with a copy button.
 *
 * Shared by `WidgetDocSection` (per-widget script tag / step snippets) and
 * `WidgetDocsDialog` (general-section code samples) so the two never drift
 * into slightly different `<pre>` styling.
 */
export const DocCode: FunctionComponent<DocCodeProps> = ({ children }) => {
    const copyToClipboard = useCopyToClipboard();

    return (
        <Box sx={{ position: 'relative', mt: 1.5 }}>
            <Box
                component='pre'
                sx={{
                    m: 0,
                    p: 2,
                    pr: 6,
                    overflowX: 'auto',
                    fontFamily: 'monospace',
                    fontSize: 13,
                    lineHeight: 1.6,
                    whiteSpace: 'pre',
                    bgcolor: 'grey.50',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: DOC_RADIUS,
                }}
            >
                {children}
            </Box>
            <Tooltip title='Code kopieren'>
                <IconButton
                    size='small'
                    aria-label='Code kopieren'
                    onClick={() => copyToClipboard(children)}
                    sx={{ position: 'absolute', top: 6, right: 6 }}
                >
                    <ClipboardDocumentIcon style={{ width: 16, height: 16 }} />
                </IconButton>
            </Tooltip>
        </Box>
    );
};

/** Inline monospace, for attribute, tag and event names. */
export const InlineCode: FunctionComponent<DocCodeProps> = ({ children }) => (
    <Box
        component='code'
        sx={{
            fontFamily: 'monospace',
            fontSize: '0.9em',
            px: 0.5,
            py: 0.25,
            borderRadius: '4px',
            bgcolor: 'grey.100',
            wordBreak: 'break-word',
        }}
    >
        {children}
    </Box>
);

/**
 * Prose from the manifest, which marks code with Markdown backticks
 * (`` `productFilter` ``). Rendered as inline code instead of raw backticks.
 */
export const DocText: FunctionComponent<DocCodeProps> = ({ children }) => (
    <>
        {children
            .split('`')
            // Odd segments sit between a pair of backticks.
            .map((part, index) =>
                index % 2 ? <InlineCode key={index}>{part}</InlineCode> : part
            )}
    </>
);
